import { defineStore } from 'pinia';
import { CotizacionMasivaService } from '@/services/cotizacion_masiva_service.js';

export const FASE_VALIDACION = 'validacion';
export const FASE_RESULTADO = 'resultado';
export const ESTADO_FINAL_COTIZADA = 'COTIZADA';
export const ESTADO_FINAL_NO_PROCESADA = 'NO_PROCESADA';

/**
 * Estado temporal de la cotización masiva.
 * - Fase 1 (validación): nada se persiste; el resultado vive acá hasta `reset()`
 *   o hasta que se valide otro archivo.
 * - Fase 2 (resultado): tras `procesarLote()` guarda la respuesta del lote.
 */
export const useCotizacionMasivaStore = defineStore('cotizacionMasiva', {
  state: () => ({
    archivoNombre: '',
    resultado: null,
    soloErrores: false,
    loading: false,
    fase: FASE_VALIDACION,
    procesando: false,
    resultadoLote: null,
    filtro: 'todos',
    decisionPendiente: false,
  }),
  getters: {
    hayResultado: (state) => state.resultado !== null,
    filas: (state) => state.resultado?.filas ?? [],
    filasErroneas() {
      return this.filas.filter((fila) => fila.estado === 'ERROR');
    },
    filasCorrectas() {
      return this.filas.filter((fila) => fila.estado === 'CORRECTA');
    },
    hayErrores() {
      return this.filasErroneas.length > 0;
    },
    filasVisibles() {
      return this.soloErrores ? this.filasErroneas : this.filas;
    },
    resumen: (state) => ({
      total: state.resultado?.totalFilas ?? 0,
      correctas: state.resultado?.filasCorrectas ?? 0,
      erroneas: state.resultado?.filasConError ?? 0,
    }),
    /** Cada fila validada enriquecida con lo que devolvió el procesamiento (si se envió). */
    filasResultado() {
      const porFila = new Map(
        (this.resultadoLote?.resultados ?? []).map((item) => [item.numeroFila, item]),
      );
      return this.filas.map((fila) => {
        const procesada = porFila.get(fila.numeroFila);
        return {
          ...fila,
          numeroLote: procesada?.numeroLote ?? null,
          codigoCotizacion: procesada?.codigoCotizacion ?? null,
          estadoFinal: procesada?.estadoFinal ?? ESTADO_FINAL_NO_PROCESADA,
          errorProceso: procesada?.error ?? '',
        };
      });
    },
    filasNoProcesadas() {
      return this.filasResultado.filter((fila) => fila.estadoFinal !== ESTADO_FINAL_COTIZADA);
    },
    filasResultadoVisibles() {
      if (this.filtro === 'generados') {
        return this.filasResultado.filter((fila) => fila.estadoFinal === ESTADO_FINAL_COTIZADA);
      }
      if (this.filtro === 'no_procesados') return this.filasNoProcesadas;
      return this.filasResultado;
    },
    resumenLote() {
      const lote = this.resultadoLote?.lote;
      return {
        numeroLote: lote?.numeroLote ?? null,
        estado: lote?.estado ?? '',
        procesadas: lote?.confirmados ?? 0,
        generadas: lote?.exitosos ?? 0,
        fallidas: lote?.conError ?? 0,
        noEnviadas: Math.max(0, this.filas.length - (lote?.confirmados ?? 0)),
      };
    },
  },
  actions: {
    async validarArchivo(file) {
      this.loading = true;
      try {
        const response = await CotizacionMasivaService.validar(file);
        this.resultado = response.data;
        this.archivoNombre = file.name;
        this.soloErrores = false;
        this.fase = FASE_VALIDACION;
        this.resultadoLote = null;
        this.filtro = 'todos';
        this.decisionPendiente = false;
      } finally {
        this.loading = false;
      }
    },
    /** Envía al backend solo las filas correctas; el cálculo masivo puede demorar. */
    async procesarLote() {
      if (this.procesando || !this.filasCorrectas.length) return;
      this.procesando = true;
      try {
        const response = await CotizacionMasivaService.procesar({
          nombreArchivo: this.archivoNombre,
          filas: this.filasCorrectas.map((fila) => ({
            numeroFila: fila.numeroFila,
            codigoProducto: fila.codigoProducto,
            moneda: fila.moneda,
            unidadMedida: fila.unidadMedida,
            cantidades: fila.cantidades,
          })),
        });
        this.resultadoLote = response.data;
        this.filtro = 'todos';
        this.decisionPendiente = false;
        this.fase = FASE_RESULTADO;
      } finally {
        this.procesando = false;
      }
    },
    setSoloErrores(valor) {
      this.soloErrores = Boolean(valor);
    },
    setFiltro(valor) {
      this.filtro = valor;
    },
    /** El diálogo de decisión lo abre el usuario a pedido, después de revisar la tabla. */
    abrirDecision() {
      this.decisionPendiente = true;
    },
    cerrarDecision() {
      this.decisionPendiente = false;
    },
    cancelarCarga() {
      this.reset();
    },
    reset() {
      this.archivoNombre = '';
      this.resultado = null;
      this.soloErrores = false;
      this.fase = FASE_VALIDACION;
      this.resultadoLote = null;
      this.filtro = 'todos';
      this.decisionPendiente = false;
    },
  },
});
